// Hard-lock gate: blocks the dashboard until first-run onboarding is recorded. The
// dashboard shell lives as `children`, so it does not mount until released — sign-out is
// the only escape (handled by the wizard + the App auth gate).
//
// The gate keys off the dedicated `firstRunCompleted` flag, NOT the read-latched
// `onboardingComplete` (`onboarded_at`): the latched flag flips the moment any source
// connects, so a user who connected a source then bailed mid-wizard would be hidden from
// onboarding forever (design §10.3 / D11). With `firstRunCompleted` — written only by the
// explicit terminal action — bailing leaves them in the partial state (the checklist), not
// trapped on the dashboard.
//
// The view is LATCHED on first load: the Google/Claude-Code branches poll the same readiness
// query, so a background completion (or a source connected mid-wizard) must NOT re-route the
// user and skip the wizard's Done screen — release happens explicitly via onComplete.
//
// Fail-open on a readiness error: a transient BFF failure must never blank the whole app for
// an authenticated user; we render the dashboard (which has its own sources/extension
// prompts) rather than trap them on a blank screen.

import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { isDevBypass } from '../../lib/auth';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { useCompleteOnboarding } from '../../hooks/useCompleteOnboarding';
import { chooseOnboardingView, type OnboardingView } from './choose-onboarding-view';
import { OnboardingWizard } from './OnboardingWizard';
import { ReturnChecklist } from './ReturnChecklist';

export function OnboardingGate({
  children,
  onActiveChange,
}: {
  children: ReactNode;
  // Fired whenever an onboarding surface (wizard/checklist) is on screen instead of the
  // dashboard. AppLayout feeds it into useSessionReplayScope so first-run onboarding gets
  // recorded even though it renders outside the router (no allowlisted hash of its own).
  onActiveChange?: (active: boolean) => void;
}) {
  const devBypass = isDevBypass();
  const { loading, data, error } = useOnboardingReadiness({ enabled: !devBypass });
  const complete = useCompleteOnboarding();
  const isMobile = useIsMobile();
  const [view, setView] = useState<OnboardingView | null>(null);

  useEffect(() => {
    if (devBypass || view !== null || loading) return;
    if (data) {
      setView(chooseOnboardingView(data));
    } else if (error) {
      setView('dashboard'); // fail-open — never trap the app on a readiness error
    }
  }, [devBypass, loading, data, error, view]);

  // Terminal release: persist first-run completion, THEN drop to the dashboard. The persisted
  // flag (not local state) is the source of truth, so a reload stays on the dashboard. We
  // await the write and only release on success — `mutateAsync` rejects on a failed POST, so
  // the caller (DoneStep / ReturnChecklist) can surface it and let the user retry instead of
  // silently releasing onboarding that was never recorded.
  const release = useCallback(async () => {
    await complete.mutateAsync();
    setView('dashboard');
  }, [complete]);

  // Report whether an onboarding surface is currently taking over the screen (wizard or the
  // returning-user checklist; the mobile "finish on your computer" fallback renders under the
  // same views). Kept before the early returns so the hook order is stable.
  const onboardingActive = !devBypass && (view === 'wizard' || view === 'checklist');
  useEffect(() => {
    onActiveChange?.(onboardingActive);
  }, [onboardingActive, onActiveChange]);

  if (devBypass) return <>{children}</>;
  if (view === null) return null; // brief: initial readiness load
  // Mobile can't connect a first source (extension + OAuth popup are desktop-only), so both
  // not-onboarded states (fresh wizard, returning checklist) would trap a phone user behind
  // an unfinishable flow. Show a static "finish on your computer" screen instead — the desktop
  // wizard path is untouched.
  if (isMobile && (view === 'wizard' || view === 'checklist')) {
    return (
      <div className="v2-app onboard-mobile">
        <div className="onboard-mobile-card">
          <div className="sheet-icon" aria-hidden="true">🖥️</div>
          <h1 className="sheet-title">Finish setting up on your computer</h1>
          <p className="sheet-body">
            Terum needs the browser extension and a sign-in popup to connect your
            first source — both only work on a computer. Open{' '}
            <strong>https://app.terum.ai</strong> on your desktop to finish, then
            come back here.
          </p>
          <button className="v2-btn" onClick={() => window.location.reload()}
            style={{ width: '100%', marginTop: 8 }}>Refresh</button>
        </div>
      </div>
    );
  }
  if (view === 'wizard') return <OnboardingWizard onComplete={release} />;
  if (view === 'checklist') return <ReturnChecklist onComplete={release} />;
  return <>{children}</>;
}
